import { DataTablePagination } from 'components/common'
import SearchBar from 'components/common/dataTable/searchBar'
import DateRangeReportForm from 'components/dateRangeReportForm/dateRangeReportForm'
import { useQuery } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Pagination } from 'types/user'
import { exportCost, fetchCosts } from 'utils/api/erpCostApi'

interface ErpCostDataType {
  _id: string
  new_cost: number | null
  change_value: number | null
  old_cost: number | null
  updatedAt: Date
}

export type ErpCostResponse = Pagination & {
  docs: ErpCostDataType[] | []
}

const ErpCost = () => {
  const [loading, setLoading] = useState(false)
  const [erpCost, setErpCost] = useState<ErpCostResponse | null>(null)

  const { getAllQueries } = useQuery()

  const { search } = useLocation()

  const getErpCost = async () => {
    setLoading(true)
    const { limit, search: SearchTerm, page } = getAllQueries()

    try {
      const { data } = await fetchCosts({
        limit: limit || 10,
        page: page || 1,
        search: SearchTerm,
      })
      setErpCost(data)
    } catch (error) {
      setErpCost(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getErpCost()
  }, [search])

  const formatCurrency = (value: number | null) => {
    if (value === null) return '-'
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(value)
  }

  const formatDate = (date: Date) => {
    if (!date) return '-'

    // Convert to Date object and format in DD/MM/YY
    const dateObj = new Date(date)
    const day = dateObj.getDate().toString().padStart(2, '0')
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
    const year = dateObj.getFullYear().toString().slice(-2)

    return `${day}/${month}/${year}`
  }

  const renderCostRows = useMemo(() => {
    if (!erpCost?.docs || erpCost.docs.length === 0) {
      return (
        <tr>
          <td colSpan={5} className="py-8 text-center text-gray-500">
            {loading ? 'Loading...' : 'No cost data found'}
          </td>
        </tr>
      )
    }

    return erpCost.docs.map((cost) => (
      <tr key={cost._id} className="border-b border-gray-200 hover:bg-gray-50">
        <td className="px-4 py-3 font-medium">{cost._id}</td>
        <td className="px-4 py-3">{formatCurrency(cost.old_cost)}</td>
        <td className="px-4 py-3">{formatCurrency(cost.new_cost)}</td>
        <td
          className={`px-4 py-3 font-semibold ${
            cost.change_value && cost.change_value > 0
              ? 'text-green-600'
              : cost.change_value && cost.change_value < 0
                ? 'text-red-600'
                : ''
          }`}
        >
          {cost.change_value !== null && cost.change_value > 0 && '+'}
          {formatCurrency(cost.change_value)}
        </td>
        <td className="px-4 py-3 text-gray-600">
          {formatDate(cost.updatedAt)}
        </td>
      </tr>
    ))
  }, [erpCost?.docs, loading])

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between">
        <div className="flex flex-col">
          <h3 className="mb-6 text-2xl font-bold">Cost Management</h3>
          <SearchBar placeholder={'Search by Item No'} disabled={loading} />
        </div>

        <DateRangeReportForm
          title="Generate Erp Cost Report"
          buttonText="Generate"
          onSubmit={async (range: { from: string; to: string }) => {
            const { from, to } = range
            await exportCost({ from, to })
          }}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="h-full max-h-[600px] overflow-y-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
                <th className="px-4 py-3">Item Number</th>
                <th className="px-4 py-3">Old Cost</th>
                <th className="px-4 py-3">New Cost</th>
                <th className="px-4 py-3">Change Value</th>
                <th className="px-4 py-3">Date Of Last Change</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">{renderCostRows}</tbody>
          </table>
        </div>
      </div>

      <div className="mt-6">
        <DataTablePagination
          currentPage={erpCost?.page}
          totalPages={erpCost?.totalPages}
          hasNextPage={erpCost?.hasNextPage}
          hasPrevPage={erpCost?.hasPrevPage}
          limit={erpCost?.limit}
          nextPage={erpCost?.nextPage}
          prevPage={erpCost?.prevPage}
        />
      </div>
    </div>
  )
}

export default ErpCost
